"use client";
import {
	AlignHorizontalJustifyCenterIcon,
	AlignHorizontalJustifyEndIcon,
	AlignHorizontalJustifyStartIcon,
	AlignVerticalJustifyCenterIcon,
	AlignVerticalJustifyEndIcon,
	AlignVerticalJustifyStartIcon,
	ChevronsLeftRightEllipsis,
	GalleryHorizontal,
	Plus,
	RectangleHorizontalIcon,
	Trash2Icon,
} from "lucide-react";
import { useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import type { NestedKeys } from "@/lib/types";
import { useStoreBuilderContext } from "@/ui/admin/store-builder/store-builder-context";
import { StoreBuilderUpload } from "@/ui/admin/store-builder/store-builder-upload";
import { BuilderInputField } from "@/ui/builder/builder-input-field";
import { debounce } from "@/ui/hooks/use-debounced-value";
import { Label } from "@/ui/shadcn/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui/shadcn/select";
import { TailwindColorPicker } from "@/ui/tailwind-color-picker";
import { CompactButtonGroup } from "../../shared/compact-button-group";
import type { YnsComponentForm } from "../types";
import type { HeroSection, HeroSlide } from "./component";
import type { HeroSectionStoreBuilderConfigFactory } from "./config";

type UpdateSlidePath = NonNullable<NestedKeys<HeroSlide>>;

export const HeroSectionForm: YnsComponentForm<typeof HeroSectionStoreBuilderConfigFactory> = ({
	data,
	theme,
}) => {
	const context = useStoreBuilderContext<typeof HeroSection>();

	const boxAlignmentOptions = [
		{ value: "fixed-width", icon: <GalleryHorizontal className="size-3" />, tooltip: "Fixed width" },
		{ value: "full-width", icon: <ChevronsLeftRightEllipsis className="size-3" />, tooltip: "Full width" },
	];

	const aspectRatioOptions = [
		{ value: "16/9", icon: <RectangleHorizontalIcon className="size-3 scale-x-75" />, tooltip: "16:9" },
		{ value: "21/9", icon: <RectangleHorizontalIcon className="size-3" />, tooltip: "21:9" },
		{ value: "32/9", icon: <RectangleHorizontalIcon className="size-3 scale-x-125" />, tooltip: "32:9" },
	];

	const horizontalAlignmentOptionsForSlides = [
		{ value: "left", icon: <AlignHorizontalJustifyStartIcon className="size-3" />, tooltip: "Align left" },
		{
			value: "center",
			icon: <AlignHorizontalJustifyCenterIcon className="size-3" />,
			tooltip: "Align center",
		},
		{ value: "right", icon: <AlignHorizontalJustifyEndIcon className="size-3" />, tooltip: "Align right" },
	];

	const verticalAlignmentOptionsForSlides = [
		{ value: "top", icon: <AlignVerticalJustifyStartIcon className="size-3" />, tooltip: "Top" },
		{ value: "middle", icon: <AlignVerticalJustifyCenterIcon className="size-3" />, tooltip: "Middle" },
		{ value: "bottom", icon: <AlignVerticalJustifyEndIcon className="size-3" />, tooltip: "Bottom" },
	];

	const initialSlides = "slides" in data ? (data.slides ?? []) : [data];
	const [slides, setSlides] = useState(initialSlides);

	const addSlide = () => {
		const newSlides = [
			...slides,
			{
				content: {
					type: "doc",
					content: [
						{
							type: "heading",
							attrs: { level: 2 },
							content: [{ type: "text", text: "Enter your title here" }],
						},
						{
							type: "paragraph",
							content: [{ type: "text", text: "Enter your description here..." }],
						},
					],
				},
				image: { src: "", alt: "" },
				button: { label: "", path: "" },
				objectPositionHorizontal: "center" as const,
				objectPositionVertical: "center" as const,
			},
		];
		setSlides(newSlides);
		context.saveSelectedPageSectionField({
			fieldPath: "slides",
			value: newSlides,
		});
	};

	const removeSlide = (index: number) => {
		if (slides.length <= 1) return; // Keep at least one slide

		const newSlides = slides.filter((_, i) => i !== index);
		setSlides(newSlides);
		context.saveSelectedPageSectionField({
			fieldPath: "slides",
			value: newSlides,
		});
	};

	const updateSlideField = (index: number, fieldPath: UpdateSlidePath, value: string, save = true) => {
		const newSlides = [...slides];

		// Handle nested paths like "button.label"
		const pathParts = fieldPath.split(".");
		let current = newSlides[index];

		for (let i = 0; i < pathParts.length - 1; i++) {
			console.log({ current, pathPart: pathParts[i] });
			if (!current) return;
			const cursor = pathParts[i];
			if (!cursor) return;

			// @ts-ignore
			if (!current[cursor]) {
				// @ts-ignore
				current[cursor] = {};
			}

			// @ts-ignore
			current = current[cursor];
		}

		// @ts-ignore
		current[pathParts[pathParts.length - 1]] = value;

		setSlides(newSlides);

		if (save) {
			context.saveSelectedPageSectionField({
				fieldPath: "slides",
				value: newSlides,
			});
		}
	};

	return (
		<div className="space-y-6">
			{/* Layout & Display Section */}
			<div>
				<div className="text-sm font-semibold text-neutral-900 mb-3 pb-2 border-b border-neutral-200">
					Layout & Display
				</div>
				<div className="space-y-4">
					<div className="grid grid-cols-2 gap-2">
						<div className="grid gap-1">
							<span className="text-xs text-neutral-600 font-medium">Width</span>
							<CompactButtonGroup
								options={boxAlignmentOptions}
								defaultValue={("size" in data ? data.size : null) ?? "fixed-width"}
								onValueChange={(value) => {
									context.saveSelectedPageSectionField({
										fieldPath: "size",
										value,
									});
								}}
								className="w-full"
							/>
						</div>
						<div className="grid gap-1">
							<span className="text-xs text-neutral-600 font-medium">Aspect Ratio</span>
							<CompactButtonGroup
								options={aspectRatioOptions}
								defaultValue={("aspectRatio" in data ? data.aspectRatio : null) ?? "16/9"}
								onValueChange={(value) => {
									context.saveSelectedPageSectionField({
										fieldPath: "aspectRatio",
										value,
									});
								}}
								className="w-full"
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Slides Section */}
			<div>
				<div className="flex items-center justify-between mb-3 pb-2 border-b border-neutral-200">
					<div className="text-sm font-semibold text-neutral-900">Slides</div>
					<Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={addSlide}>
						<Plus className="h-3 w-3 mr-1" />
						Add Slide
					</Button>
				</div>
				<div className="space-y-4">
					<Accordion type="multiple" className="w-full space-y-3">
						{slides.map((slide, index) => (
							<AccordionItem key={index} value={`slide-${index}`} className="border rounded-md bg-white">
								<AccordionTrigger className="flex items-center bg-neutral-50 p-3 hover:no-underline rounded-t-md">
									<h4 className="text-sm font-medium text-neutral-900">Slide {index + 1}</h4>
								</AccordionTrigger>
								<AccordionContent>
									<div className="p-4 space-y-6">
										{/* Alignment Fields */}
										<div className="grid grid-cols-2 gap-2">
											<div className="grid gap-1">
												<span className="text-xs text-neutral-600 font-medium">Horizontal Alignment</span>
												<CompactButtonGroup
													options={horizontalAlignmentOptionsForSlides}
													defaultValue={slide.boxAlignment}
													onValueChange={(value) => updateSlideField(index, "boxAlignment", value)}
													className="w-full"
												/>
											</div>
											<div className="grid gap-1">
												<span className="text-xs text-neutral-600 font-medium">Vertical Alignment</span>
												<CompactButtonGroup
													options={verticalAlignmentOptionsForSlides}
													defaultValue={slide.alignmentVertical}
													onValueChange={(value) => updateSlideField(index, "alignmentVertical", value)}
													className="w-full"
												/>
											</div>
										</div>

										{/* Slide Colors */}
										<div className="grid gap-2 lg:grid-cols-2">
											<Label className="grid gap-1">
												<span className="text-xs text-neutral-600 font-medium">Background Color</span>
												<TailwindColorPicker
													defaultValue={slide.theme?.backgroundColor ?? undefined}
													onValueChange={debounce(
														(value) => {
															updateSlideField(index, "theme.backgroundColor", value ?? "");
														},
														300,
														{ trailing: true },
													)}
												/>
											</Label>
											<Label className="grid gap-1">
												<span className="text-xs text-neutral-600 font-medium">Text Color</span>
												<TailwindColorPicker
													defaultValue={slide.theme?.color ?? undefined}
													onValueChange={debounce(
														(value) => {
															updateSlideField(index, "theme.color", value ?? "");
														},
														300,
														{ trailing: true },
													)}
												/>
											</Label>
										</div>

										{/* Image/Video */}
										<div>
											<h5 className="text-sm font-medium text-neutral-900 mb-3 pb-2 border-b border-neutral-200">
												Image/Video
											</h5>
											<div className="space-y-4">
												<div className="text-xs text-neutral-600">
													Images: PNG, JPG, WebP, AVIF ⋅ Videos: MP4, WebM, OGG ⋅ Dimensions: 1920x1080 (16:9)
													⋅ Max size: 25 MB
												</div>
												<StoreBuilderUpload
													width={1920}
													height={1080}
													className=""
													fieldPathPrefix={`slides.${index}.image.`}
													images={[slide.image?.src].filter(Boolean)}
													acceptVideo={true}
													onUpload={(src) => {
														// @todo videos should not be saved under `image.src`
														updateSlideField(index, "image.src", src, false);
													}}
													onDelete={() => {
														// @todo videos should not be saved under `image.src`
														updateSlideField(index, "image.src", "", false);
													}}
												/>

												<div className="grid grid-cols-2 gap-2">
													<div className="grid gap-1">
														<span className="text-xs text-neutral-600 font-medium">Horizontal Alignment</span>
														<CompactButtonGroup
															options={[
																{
																	value: "left",
																	icon: <AlignHorizontalJustifyStartIcon className="size-3" />,
																	tooltip: "Left",
																},
																{
																	value: "center",
																	icon: <AlignHorizontalJustifyCenterIcon className="size-3" />,
																	tooltip: "Center",
																},
																{
																	value: "right",
																	icon: <AlignHorizontalJustifyEndIcon className="size-3" />,
																	tooltip: "Right",
																},
															]}
															defaultValue={slide.objectPositionHorizontal ?? "center"}
															onValueChange={(value) =>
																updateSlideField(index, "objectPositionHorizontal", value)
															}
															className="w-full"
														/>
													</div>
													<div className="grid gap-1">
														<span className="text-xs text-neutral-600 font-medium">Vertical Alignment</span>
														<CompactButtonGroup
															options={[
																{
																	value: "top",
																	icon: <AlignVerticalJustifyStartIcon className="size-3" />,
																	tooltip: "Top",
																},
																{
																	value: "center",
																	icon: <AlignVerticalJustifyCenterIcon className="size-3" />,
																	tooltip: "Center",
																},
																{
																	value: "bottom",
																	icon: <AlignVerticalJustifyEndIcon className="size-3" />,
																	tooltip: "Bottom",
																},
															]}
															defaultValue={slide.objectPositionVertical ?? "center"}
															onValueChange={(value) =>
																updateSlideField(index, "objectPositionVertical", value)
															}
															className="w-full"
														/>
													</div>
												</div>
											</div>
										</div>

										{/* Buttons */}
										<div>
											<h5 className="text-sm font-medium text-neutral-900 mb-3 pb-2 border-b border-neutral-200">
												Buttons
											</h5>
											<div className="space-y-6">
												{/* Button Style Options */}
												<div className="space-y-4">
													<div className="grid gap-1">
														<span className="text-xs text-neutral-600 font-medium">Button Alignment</span>
														<CompactButtonGroup
															options={[
																{
																	value: "left",
																	icon: <AlignHorizontalJustifyStartIcon className="size-3" />,
																	tooltip: "Left",
																},
																{
																	value: "center",
																	icon: <AlignHorizontalJustifyCenterIcon className="size-3" />,
																	tooltip: "Center",
																},
																{
																	value: "right",
																	icon: <AlignHorizontalJustifyEndIcon className="size-3" />,
																	tooltip: "Right",
																},
															]}
															defaultValue={slide.textAlignment ?? "left"}
															onValueChange={(value) => updateSlideField(index, "textAlignment", value)}
															className="w-full"
														/>
													</div>
													<div className="grid grid-cols-2 gap-2">
														<div className="grid gap-1">
															<span className="text-xs text-neutral-600 font-medium">Button Radius</span>
															<Select
																defaultValue={slide.theme?.buttonRadius ?? "default"}
																onValueChange={debounce(
																	(value) => {
																		updateSlideField(index, "theme.buttonRadius", value ?? "default");
																	},
																	300,
																	{ trailing: true },
																)}
															>
																<SelectTrigger className="w-full">
																	<SelectValue placeholder="Select radius" />
																</SelectTrigger>
																<SelectContent>
																	<SelectItem value="default">Default</SelectItem>
																	<SelectItem value="none">None</SelectItem>
																	<SelectItem value="sm">Small</SelectItem>
																	<SelectItem value="md">Medium</SelectItem>
																	<SelectItem value="lg">Large</SelectItem>
																	<SelectItem value="full">Full</SelectItem>
																</SelectContent>
															</Select>
														</div>
														<div className="grid gap-1">
															<span className="text-xs text-neutral-600 font-medium">Button Border</span>
															<Select
																defaultValue={slide.theme?.buttonBorder ?? "default"}
																onValueChange={debounce(
																	(value) => {
																		updateSlideField(index, "theme.buttonBorder", value ?? "default");
																	},
																	300,
																	{ trailing: true },
																)}
															>
																<SelectTrigger className="w-full">
																	<SelectValue placeholder="Select border" />
																</SelectTrigger>
																<SelectContent>
																	<SelectItem value="default">Default</SelectItem>
																	<SelectItem value="none">None</SelectItem>
																	<SelectItem value="sm">Small</SelectItem>
																	<SelectItem value="md">Medium</SelectItem>
																	<SelectItem value="lg">Large</SelectItem>
																</SelectContent>
															</Select>
														</div>
													</div>
												</div>

												{/* Button Accordions */}
												<Accordion type="multiple" className="w-full">
													<AccordionItem value={`slide-${index}-primary-button`} className="border-none">
														<AccordionTrigger className="py-2 text-sm font-medium">
															Primary Button
														</AccordionTrigger>
														<AccordionContent>
															<div className="space-y-4 pt-2">
																<BuilderInputField
																	label="Button Label"
																	defaultValue={slide.button?.label ?? ""}
																	onValueChange={(value) => updateSlideField(index, "button.label", value)}
																/>
																<BuilderInputField
																	label="Button Link"
																	defaultValue={slide.button?.path ?? ""}
																	onValueChange={(value) => updateSlideField(index, "button.path", value)}
																/>

																{/* Primary Button Colors */}
																<div className="grid gap-2 lg:grid-cols-2">
																	<Label className="grid gap-1">
																		<span className="text-xs text-neutral-600 font-medium">
																			Button Background
																		</span>
																		<TailwindColorPicker
																			defaultValue={slide.theme?.buttonBackgroundColor ?? undefined}
																			onValueChange={debounce(
																				(value) => {
																					updateSlideField(index, "theme.buttonBackgroundColor", value ?? "");
																				},
																				300,
																				{ trailing: true },
																			)}
																		/>
																	</Label>
																	<Label className="grid gap-1">
																		<span className="text-xs text-neutral-600 font-medium">
																			Button Text Color
																		</span>
																		<TailwindColorPicker
																			defaultValue={slide.theme?.buttonTextColor ?? undefined}
																			onValueChange={debounce(
																				(value) => {
																					updateSlideField(index, "theme.buttonTextColor", value ?? "");
																				},
																				300,
																				{ trailing: true },
																			)}
																		/>
																	</Label>
																	<Label className="grid gap-1">
																		<span className="text-xs text-neutral-600 font-medium">
																			Button Hover Background
																		</span>
																		<TailwindColorPicker
																			defaultValue={slide.theme?.buttonHoverBackgroundColor ?? undefined}
																			onValueChange={debounce(
																				(value) => {
																					updateSlideField(
																						index,
																						"theme.buttonHoverBackgroundColor",
																						value ?? "",
																					);
																				},
																				300,
																				{ trailing: true },
																			)}
																		/>
																	</Label>
																	<Label className="grid gap-1">
																		<span className="text-xs text-neutral-600 font-medium">
																			Button Border Color
																		</span>
																		<TailwindColorPicker
																			defaultValue={slide.theme?.buttonBorderColor ?? undefined}
																			onValueChange={debounce(
																				(value) => {
																					updateSlideField(index, "theme.buttonBorderColor", value ?? "");
																				},
																				300,
																				{ trailing: true },
																			)}
																		/>
																	</Label>
																</div>
															</div>
														</AccordionContent>
													</AccordionItem>

													<AccordionItem value={`slide-${index}-secondary-button`} className="border-none">
														<AccordionTrigger className="py-2 text-sm font-medium">
															Secondary Button
														</AccordionTrigger>
														<AccordionContent>
															<div className="space-y-4 pt-2">
																<BuilderInputField
																	label="Button Label"
																	defaultValue={slide.button2?.label ?? ""}
																	onValueChange={(value) => updateSlideField(index, "button2.label", value)}
																/>
																<BuilderInputField
																	label="Button Link"
																	defaultValue={slide.button2?.path ?? ""}
																	onValueChange={(value) => updateSlideField(index, "button2.path", value)}
																/>

																{/* Secondary Button Colors */}
																<div className="grid gap-2 lg:grid-cols-2">
																	<Label className="grid gap-1">
																		<span className="text-xs text-neutral-600 font-medium">
																			Button Background
																		</span>
																		<TailwindColorPicker
																			defaultValue={slide.theme?.button2BackgroundColor ?? undefined}
																			onValueChange={debounce(
																				(value) => {
																					updateSlideField(
																						index,
																						"theme.button2BackgroundColor",
																						value ?? "",
																					);
																				},
																				300,
																				{ trailing: true },
																			)}
																		/>
																	</Label>
																	<Label className="grid gap-1">
																		<span className="text-xs text-neutral-600 font-medium">
																			Button Text Color
																		</span>
																		<TailwindColorPicker
																			defaultValue={slide.theme?.button2TextColor ?? undefined}
																			onValueChange={debounce(
																				(value) => {
																					updateSlideField(index, "theme.button2TextColor", value ?? "");
																				},
																				300,
																				{ trailing: true },
																			)}
																		/>
																	</Label>
																	<Label className="grid gap-1">
																		<span className="text-xs text-neutral-600 font-medium">
																			Button Hover Background
																		</span>
																		<TailwindColorPicker
																			defaultValue={slide.theme?.button2HoverBackgroundColor ?? undefined}
																			onValueChange={debounce(
																				(value) => {
																					updateSlideField(
																						index,
																						"theme.button2HoverBackgroundColor",
																						value ?? "",
																					);
																				},
																				300,
																				{ trailing: true },
																			)}
																		/>
																	</Label>
																	<Label className="grid gap-1">
																		<span className="text-xs text-neutral-600 font-medium">
																			Button Border Color
																		</span>
																		<TailwindColorPicker
																			defaultValue={slide.theme?.button2BorderColor ?? undefined}
																			onValueChange={debounce(
																				(value) => {
																					updateSlideField(index, "theme.button2BorderColor", value ?? "");
																				},
																				300,
																				{ trailing: true },
																			)}
																		/>
																	</Label>
																</div>
															</div>
														</AccordionContent>
													</AccordionItem>
												</Accordion>
											</div>
										</div>

										{/* Delete Slide Button */}
										{slides.length > 1 && (
											<Button
												variant="outline"
												size="sm"
												onClick={() => removeSlide(index)}
												className="w-full bg-red-50 border-red-200 text-red-700 hover:bg-red-100 hover:border-red-300"
											>
												<Trash2Icon className="h-4 w-4 mr-2" />
												Remove Slide
											</Button>
										)}
									</div>
								</AccordionContent>
							</AccordionItem>
						))}
					</Accordion>
				</div>
			</div>
		</div>
	);
};
